import type {
  Order,
  OperationOpen,
  OperationClosed,
  Operation,
  TournamentEvent,
  TournamentEventType,
} from '../sdk'

/**
 * Lifecycle marker for event history (rendered in Phase 4).
 * Deterministic ID prevents duplicate markers from out-of-order events.
 */
export interface LifecycleMarker {
  /** Unique: `${operationId}::${eventType}::${timestamp}` */
  id: string
  operationId: string
  eventType: TournamentEventType
  symbol: string
  side: 'BUY' | 'SELL'
  /** Price at event time (undefined for market orders) */
  price: number | undefined
  /** ISO timestamp from backend event */
  timestamp: string
}

/**
 * Response from server after a fast-forward operation.
 * Contains the complete state snapshot at the target replay time.
 *
 * May contain canonical fields (metrics, remainingTime as Go duration)
 * or legacy fields (balance, equity, remainingMatchTime as milliseconds).
 */
export interface FastForwardResponse {
  /** All operations at the jumped time */
  operations: Operation[]
  /** Account balance in base currency units (legacy fallback field) */
  balance?: number
  /** Account equity (legacy fallback field) */
  equity?: number
  /** Current replay time (ISO timestamp) */
  currentReplayTime: string
  /** Optional display-domain replay timestamp used as the preferred chart sync anchor */
  currentReplayTimeDisplay?: string
  /** Remaining time until match end (milliseconds, legacy) */
  remainingMatchTime?: number
  /** Canonical remaining time as Go duration string (e.g., "1h0m0s", "30m0s", "45s") */
  remainingTime?: string
  /** Canonical metrics object with decimal-string account values */
  metrics?: {
    balance: string | number
    equity: string | number
  }
}

/**
 * Normalized fast-forward response after contract compatibility processing.
 * Ready for store state application.
 */
export interface NormalizedFastForwardResponse {
  operations: Operation[]
  currentReplayTime: string
  currentReplayTimeDisplay?: string
  remainingMatchTimeMs: number
  balance: number
  equity: number
}

/**
 * Single source of truth for all trading operations and market data.
 * Consumed by providers in Phase 3, marker renderer in Phase 4.
 */
export interface OperationStoreState {
  /** Canonical operation map: keyed by order ID */
  operationsById: Map<string, Operation>

  /** Fast partition indexes (operation IDs) */
  pendingIds: Set<string>
  openIds: Set<string>
  closedIds: Set<string>

  /** Latest prices: keyed by backend symbol (e.g., "EURUSD") */
  latestPricesBySymbol: Record<string, number>

  /** Ordered lifecycle markers (insertion order = chronological) */
  lifecycleMarkers: LifecycleMarker[]

  /** For O(1) marker dedup */
  markerIdSet: Set<string>

  /** Whether initial hydration from listAllOperations completed */
  hydrated: boolean

  /** Current replay timestamp from fast-forward (undefined for live or initial state) */
  currentReplayTime: string | undefined

  /** Optional display-domain replay timestamp from fast-forward (preferred chart anchor) */
  currentReplayTimeDisplay: string | undefined

  /** Remaining match time in milliseconds (undefined for live or initial state) */
  remainingMatchTime: number | undefined
}

/**
 * Actions dispatched to the store reducer.
 */
export type StoreAction =
  | { type: 'HYDRATE'; operations: Operation[] }
  | { type: 'TOURNAMENT_EVENT'; event: TournamentEvent }
  | { type: 'PRICE_UPDATED'; symbol: string; price: number }
  | { type: 'REQUEST_ACK_POSITION_OPENED'; data: OperationOpen }
  | { type: 'REQUEST_ACK_ORDER_PLACED'; data: Order }
  | { type: 'REQUEST_ACK_ORDER_CANCELLED'; data: Order }
  | { type: 'REQUEST_ACK_POSITION_CLOSED'; data: OperationClosed }
  | { type: 'REQUEST_ACK_OPERATION_UPDATED'; data: Operation }
  | { type: 'FAST_FORWARD_RESPONSE'; data: NormalizedFastForwardResponse }
  | { type: 'RESET' }

/** Maximum lifecycle markers to retain (oldest-first eviction) */
export const MARKER_CAP = 2000
